import type { LoginRequest, LoginResponse } from "../types/auth.type";
import type { IAuthRepository } from "../repositories/AuthRepository";
import { setToken, setRefreshToken, setStoredUser } from "../utils/auth";

export interface ILoginUseCase {
  execute(credentials: LoginRequest): Promise<LoginResponse>;
}

export class LoginUseCase implements ILoginUseCase {
  constructor(private readonly authRepository: IAuthRepository) {}

  async execute(credentials: LoginRequest): Promise<LoginResponse> {
    if (!credentials.email || !credentials.password) {
      throw new Error("Email and password are required");
    }
    if (!this.isValidEmail(credentials.email)) {
      throw new Error("Invalid email format");
    }
    try {
      const response = await this.authRepository.login(credentials);
      setToken(response.accessToken);
      setRefreshToken(response.refreshToken);
      setStoredUser(response.user);
      return response;
    } catch (error: unknown) {
      const apiError = error as {
        message?: string;
        code?: string;
        response?: { data?: { message?: string; code?: string } };
      };
      const apiMessage = apiError.response?.data?.message ?? apiError.message;
      const loginError = new Error(
        apiMessage ??
          "Login failed. Please check your credentials and try again.",
      ) as Error & { code?: string };
      loginError.code = apiError.response?.data?.code ?? apiError.code;
      throw loginError;
    }
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

export const createLoginUseCase = (
  authRepository: IAuthRepository,
): LoginUseCase => {
  return new LoginUseCase(authRepository);
};
